import Controller from '@ember/controller';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import { action, get } from '@ember/object';
import { reads, equal } from 'macro-decorators';

export default class LocationOverviewIntegrationsIndexController extends Controller {
  @service state;
  @service router;
  @service featureFlags;
  @service integrationsManager;
  @service flashMessages;
  @service store;

  @reads('state.currentCompany') currentCompany;
  @reads('currentCompany.saml.content') samlIntegration;
  @reads('currentCompany.googleApp.content') googleIntegration;
  @reads('currentCompany.oneLogin.content') oneLoginIntegration;
  @reads('currentCompany.oktaIntegration.content') oktaIntegration;
  @equal('currentCompany.scimIntegration.provider', 'onelogin') oneLoginSCIM;
  @equal('currentCompany.scimIntegration.provider', 'okta') oktaSCIM;
  @equal('currentCompany.scimIntegration.provider', 'azure') entraIdSCIM; // microsoft renamed azure to entra id
  @equal('currentCompany.scimIntegration.provider', 'centrify') delineaSCIM; // centrify renamed to delinea
  @reads('currentCompany.scimIntegration.content') scimIntegration;

  get canAccessDirectorySync() {
    const isFeatureGatingEnabled = this.featureFlags.isEnabled('app-store-integrations-feature-gating');
    if (isFeatureGatingEnabled) {
      return this.state.features?.canAccessDirectorySsoIntegrations;
    }
    return this.state.features?.canAccessDirectorySync;
  }

  get canAccessSaml() {
    const isFeatureGatingEnabled = this.featureFlags.isEnabled('app-store-integrations-feature-gating');
    if (isFeatureGatingEnabled) {
      return this.state.features?.canAccessDirectorySsoIntegrations;
    }
    return this.state.features?.canAccessSaml;
  }

  get hasScimIntegration() {
    return this.featureFlags.isEnabled('scimIntegration', {
      created_at: this.currentCompany.createdAt,
    });
  }

  // this is to return the Active Directory plugin install if it exists
  get activeSyncPluginInstalls() {
    // find plugins in the sync category
    const syncPlugins = this.model.plugins?.filterBy('category', 'sync');
    const pluginInstalls = this.model.pluginInstalls;

    // find active installs that belong to any plugin in the sync category
    const syncPluginInstalls = pluginInstalls.filter((install) => {
      return syncPlugins.findBy('id', get(install, 'plugin.id')) !== undefined;
    });

    return syncPluginInstalls.filter(function (install) {
      return install.status === 'active' || install.status === 'pending';
    });
  }

  get currentSyncIntegration() {
    let currentIntegration = false;
    const activeSyncPluginInstalls = this.activeSyncPluginInstalls;

    if (this.googleIntegration) {
      currentIntegration = 'Google';
    }

    if (this.oneLoginIntegration || this.oneLoginSCIM) {
      currentIntegration = 'OneLogin';
    }

    if (this.oktaIntegration || this.oktaSCIM) {
      currentIntegration = 'Okta';
    }

    if (this.entraIdSCIM) {
      currentIntegration = 'Entra ID';
    }

    if (this.delineaSCIM) {
      currentIntegration = 'Delinea';
    }

    // This would say e.g. 'OKTA SCIM' if you installed the SCIM
    // version and then lost privileges for SCIM.
    if (!this.featureFlags.isEnabled('scimIntegration')) {
      if (this.scimIntegration) {
        currentIntegration += ' SCIM';
      }
    }

    if (activeSyncPluginInstalls.length > 0) {
      currentIntegration = activeSyncPluginInstalls[0].plugin?.name;
    }

    return currentIntegration;
  }

  get hasSync() {
    return this.currentSyncIntegration !== false;
  }

  get isGlobalOverview() {
    return this.router.currentRouteName?.includes('location-overview');
  }

  get scimSettingsRoute() {
    return `${this.isGlobalOverview ? 'location-overview.' : ''}employees.scim-settings`;
  }

  get employeeIntegrationsRoute() {
    return `${this.isGlobalOverview ? 'location-overview.' : ''}employees.integrations`;
  }

  @action
  goToPluginInstallConfig(integration) {
    this.router.transitionTo(this.scimSettingsRoute, { queryParams: { integration } });
  }

  installGoogleAppsTask = task(async () => {
    const integrationsManager = this.integrationsManager;
    // Clean any existing error
    this.store.unloadAll('google-app');

    try {
      await integrationsManager.connectGoogleApps(this.state.currentCompany);
      this.state.currentCompany.reload();
      this.router.transitionTo(this.scimSettingsRoute);
    } catch (response) {
      let message = "Sorry, we couldn't connect your account.";
      if (response?.errors) {
        const [error] = response.errors;
        if (error) {
          message = error.messages.join(', ');
        }
      }
      this.flashMessages.showAndHideFlash('error', message);
    }
  });

  installOneLoginTask = task(async () => {
    this.store.createRecord('one-login', { company: this.state.currentCompany });
    await this.router.transitionTo(this.scimSettingsRoute, {
      queryParams: { from: this.employeeIntegrationsRoute, integration: 'one-login' },
    });
  });

  installOktaTask = task(async () => {
    this.store.createRecord('okta-integration', { company: this.state.currentCompany });
    await this.router.transitionTo(this.scimSettingsRoute, {
      queryParams: { from: this.employeeIntegrationsRoute, integration: 'okta' },
    });
  });

  installScimTask = task(async (provider) => {
    this.store.createRecord('scim-integration', { company: this.state.currentCompany, provider }).save();
    await this.router.transitionTo(this.scimSettingsRoute, {
      queryParams: { from: this.employeeIntegrationsRoute, integration: 'scim' },
    });
  });

  installSamlTask = task(async () => {
    this.store.createRecord('saml', { company: this.state.currentCompany });
    await this.router.transitionTo(this.scimSettingsRoute, {
      queryParams: { from: this.employeeIntegrationsRoute, integration: 'saml' },
    });
  });
}
